"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.googleAdsGetProductGroupDetails = exports.googleAdsGetProductGroupDetailsResultSchema = exports.googleAdsGetProductGroupDetailsArgsSchema = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
var googleAdsGetProductGroupDetailsInputSchema = zod_1.z
    .object({
    customerId: zod_1.z
        .string()
        .nullish()
        .describe('ONLY provide if user explicitly specifies a Google Ads customer ID. DO NOT generate or guess customer IDs. Leave empty to automatically use the selected account. Must be provided by user - never invent random numbers.'),
    campaignId: zod_1.z
        .string()
        .nullish()
        .describe('Specific Shopping campaign ID to analyze. If not provided, returns data for all Shopping campaigns in the account.'),
    adGroupId: zod_1.z
        .string()
        .nullish()
        .describe('Specific ad group ID to analyze. If not provided, returns data for all ad groups.'),
    productGroupId: zod_1.z
        .string()
        .nullish()
        .describe('Specific product group ID to analyze. If not provided, returns data for all product groups.'),
    startDate: zod_1.z
        .string()
        .nullish()
        .describe('Start date for performance data in YYYY-MM-DD format. e.g. 2025-08-01. Optional.'),
    endDate: zod_1.z
        .string()
        .nullish()
        .describe('End date for performance data in YYYY-MM-DD format. e.g. 2025-08-31. Optional.'),
    metrics: zod_1.z
        .array(zod_1.z.enum(['impressions', 'clicks', 'cost', 'conversions', 'ctr', 'cpc', 'cpm']))
        .nullish()
        .default(['impressions', 'clicks', 'cost', 'ctr'])
        .describe('Metrics to include in the response. Defaults to basic metrics.'),
    maxItems: zod_1.z
        .number()
        .min(500)
        .default(500)
        .describe('The maximum number of product groups to fetch. Unless specified, defaults to 500. Minimum is 500.'),
    includeDisabled: zod_1.z
        .boolean()
        .default(false)
        .describe('Include paused, removed, or disabled product groups in results. Set to true to show all product groups regardless of status. Defaults to false.'),
    options: zod_1.z
        .object({
        failIfEmpty: zod_1.z
            .boolean()
            .default(true)
            .describe('Fail if no product group data is found. Unless specified, defaults to true.'),
        groupByDay: zod_1.z
            .boolean()
            .default(false)
            .describe('Group performance data by day. Unless specified, defaults to false.'),
    })
        .describe('Performance query options'),
})
    .describe('Get performance metrics for product groups in Shopping campaigns. Shows which product categories, brands, or conditions perform best.');
exports.googleAdsGetProductGroupDetailsArgsSchema = (0, common_1.GoogleAdsNodeArgsSchema)(googleAdsGetProductGroupDetailsInputSchema);
var googleAdsGetProductGroupDetailsResponseSchema = zod_1.z.array(zod_1.z.object({
    productGroupId: zod_1.z.string().describe('The product group ID'),
    productGroupName: zod_1.z.string().nullish().describe('The product group name or category'),
    productGroupType: zod_1.z
        .string()
        .nullish()
        .describe('The product group type (e.g., CATEGORY, BRAND, CONDITION)'),
    adGroupId: zod_1.z.string().nullish().describe('The ad group ID'),
    adGroupName: zod_1.z.string().nullish().describe('The ad group name'),
    campaignId: zod_1.z.string().nullish().describe('The campaign ID'),
    campaignName: zod_1.z.string().nullish().describe('The campaign name'),
    status: zod_1.z.string().nullish().describe('Product group status'),
    bid: zod_1.z.number().nullish().describe('Product group bid amount'),
    performance: zod_1.z
        .object({
        impressions: zod_1.z.number().nullish(),
        clicks: zod_1.z.number().nullish(),
        cost: zod_1.z.number().nullish(),
        conversions: zod_1.z.number().nullish(),
        ctr: zod_1.z.number().nullish(),
        cpc: zod_1.z.number().nullish(),
        cpm: zod_1.z.number().nullish(),
        conversionRate: zod_1.z.number().nullish(),
        conversionValue: zod_1.z.number().nullish(),
        roas: zod_1.z.number().nullish(),
    })
        .describe('Performance metrics for the product group'),
    dailyBreakdown: zod_1.z
        .array(zod_1.z.object({
        date: zod_1.z.string(),
        impressions: zod_1.z.number().nullish(),
        clicks: zod_1.z.number().nullish(),
        cost: zod_1.z.number().nullish(),
    }))
        .nullish()
        .describe('Daily performance breakdown if groupByDay is enabled'),
}));
exports.googleAdsGetProductGroupDetailsResultSchema = googleAdsGetProductGroupDetailsResponseSchema;
exports.googleAdsGetProductGroupDetails = (0, common_1.createEmitModeToolMetadata)({
    id: 'googleAdsGetProductGroupDetails',
    displayName: 'Google Ads Product Group Performance',
    displayDescription: 'Get performance metrics for product groups in Shopping campaigns. Shows which product categories, brands, or conditions perform best. Automatically uses the selected account unless user provides specific customer ID.',
    category: 'ads',
    color: common_1.BRAND_COLORS.GOOGLE_ADS,
    backgroundColor: (0, common_1.createBgColor)(common_1.BRAND_COLORS.GOOGLE_ADS),
    icon: 'google_ads',
    argsSchema: exports.googleAdsGetProductGroupDetailsArgsSchema,
}, googleAdsGetProductGroupDetailsResponseSchema);
